// The HTML of a post body, as the feed renders it (dangerouslySetInnerHTML).
// Kept free of React and Capacitor so it can be unit-tested.

// A real tag: "<p>", "</strong>", "<img …>", not the "<" of "3 < 4".
const TAG = /(<\/?[a-zA-Z][^>]*>)/;
const LINK = /\bhttps:\/\/[^\s<>]+\b/gi;
// Longest emoji sequence kept from an <img alt> (family emoji with joiners).
const EMOJI_ALT_MAX = 12;

const ENTITIES = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " " };

function decodeEntities(value) {
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, code) => {
    if (code[0] === "#") {
      const point = code[1].toLowerCase() === "x" ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isFinite(point) ? String.fromCodePoint(point) : entity;
    }
    return ENTITIES[code.toLowerCase()] ?? entity;
  });
}

function escapeText(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Pictures inside the text. Teams (and Outlook, Word…) copy emoji as small
 * images, e.g. <img src="https://teams.public.onecdn.static.microsoft/…/1f440_eyes/…png" alt="👀">,
 * and the CRM editor kept them (post 13298, 22/10/2026). The emoji itself
 * stays; any other inline picture goes: a post's pictures have their own field.
 */
export function emojiImagesToText(html) {
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    const match = tag.match(/\balt\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
    const alt = decodeEntities((match && (match[1] ?? match[2])) || "").trim();
    if (!alt || [...alt].length > EMOJI_ALT_MAX || /[\p{L}\p{N}]/u.test(alt)) {
      return "";
    }
    return escapeText(alt);
  });
}

/**
 * Bare https:// addresses in the text become links. Only in the text between
 * tags: an address inside an attribute (an <img src>) or inside a link the
 * editor already made is left alone; rewriting those broke the markup and
 * showed it as text.
 */
export function linkifyText(html) {
  let insideLink = 0;
  return html
    .split(TAG)
    .map((part) => {
      if (TAG.test(part)) {
        if (/^<(a|ion-button)\b/i.test(part)) insideLink += 1;
        else if (/^<\/(a|ion-button)\s*>/i.test(part)) insideLink = Math.max(0, insideLink - 1);
        return part;
      }
      if (insideLink) return part;
      return part.replace(
        LINK,
        (link) => `<a href="${link}" target="_blank" rel="noopener noreferrer">${link}</a>`,
      );
    })
    .join("");
}

export function postBodyHtml(text) {
  if (!text) return "";

  // Posts written in the app are PLAIN TEXT: the composer is a textarea, so the
  // author's paragraph breaks arrive as "\n" / "\n\n". This body is rendered
  // through dangerouslySetInnerHTML, and HTML collapses every run of whitespace
  // into a single space — which is why a post written as several paragraphs came
  // out as one unbroken block. On production 20% of recent posts are plain text
  // like this.
  //
  // The other posts come from the CRM's rich editor and are real HTML that
  // already carries its own <p>/<br>. Those are pretty-printed, so converting
  // their newlines too would turn the whitespace *between* tags into spurious
  // blank lines. Hence: only convert when the body has no block markup of its
  // own. Checked here, before the link passes below inject any tags.
  const isPlainText = !/<(p|br|div|ul|ol|li|h[1-6]|blockquote|table)\b/i.test(text);

  let html = emojiImagesToText(text)
    .replace(/<a\s+[^>]*href=['"][^'"]*https:\/\/[^'"]*['"][^>]*>/g, (match) => {
      return match.replace(/https:\/\//g, "");
    })
    .replace(/<a\s+[^>]*href=/g, '<ion-button onClick="handleButtonLink(this)" data-href=')
    .replace(/<\/a>/g, "</ion-button>");

  html = linkifyText(html);

  // Last, so the <br> we introduce cannot interfere with the link matching
  // above. "\n\n" becomes two <br> and renders as the blank line the author
  // typed between paragraphs.
  if (isPlainText) {
    html = html.replace(/\r\n?/g, "\n").replace(/\n/g, "<br />");
  }

  return html;
}
